import React, { useState } from 'react';
import { ShieldCheck, Search, Filter, Download, Eye, CheckCircle2, XCircle, Clock, FileText, AlertCircle, AlertTriangle, ExternalLink, Brain, HelpCircle, X } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
interface AdminVerificationManagerProps {
  verificationQueue: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminVerificationManager({
  verificationQueue,
  astrologers,
  setActiveTab,
  setDetailPanel,
  success
}: AdminVerificationManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    docType: 'all',
    language: 'all',
    country: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [viewingDoc, setViewingDoc] = useState<{
    type: string;
    lines: string[];
    icon: string;
    bg: string;
  } | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});
  // --- MOCK DATA ENRICHMENT ---
  const enrichedQueue = verificationQueue.map((v, i) => {
    const isRisky = i % 5 === 0;
    const isFast = i % 3 === 0;
    const isLong = i % 4 === 0;
    const itemId = v.id || `800${i}`;
    return {
      ...v,
      id: itemId,
      status: statusOverrides[itemId] || v.status || (i % 4 === 0 ? 'needs_info' : 'pending'),
      docTypes: ['Паспорт', i % 2 === 0 ? 'Диплом' : 'Сертификат', i % 3 === 0 ? 'Видео-презентация' : null].filter(Boolean),
      lastAction: i % 2 === 0 ? 'Астролог загрузил документ' : 'Админ запросил уточнение',
      moderator: i % 3 === 0 ? 'Система' : 'Админ #1',
      waitTime: isFast ? '2 часа' : isLong ? '3 дня' : '1 день',
      waitStatus: isFast ? 'good' : isLong ? 'critical' : 'warning',
      aiConfidence: 85 + i % 15,
      riskIndicators: isRisky ? ['повторная заявка', 'подозрительный IP'] : [],
      history: [{
        date: '12 Окт 10:00',
        text: 'Заявка создана',
        actor: 'Астролог'
      }, {
        date: '13 Окт 14:30',
        text: 'Загружен диплом',
        actor: 'Астролог'
      }, ...(i % 2 !== 0 ? [{
        date: '14 Окт 09:15',
        text: 'Запрошено уточнение по паспорту',
        actor: 'Админ #1'
      }] : []), ...(i % 2 !== 0 ? [{
        date: '15 Окт 11:20',
        text: 'Документ обновлён',
        actor: 'Астролог'
      }] : [])]
    };
  });
  // KPI Calculations
  const pendingReview = enrichedQueue.filter(v => v.status === 'pending').length;
  const needsMoreInfo = enrichedQueue.filter(v => v.status === 'needs_info').length;
  const approvedCount = enrichedQueue.filter(v => v.status === 'approved').length + 12;
  const rejectedCount = enrichedQueue.filter(v => v.status === 'rejected').length + 3;
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, _textClass: string, statusFilter: string) => <Card padding="sm" hoverable className={`rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group transition-all ${filters.status === statusFilter ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''}`} onClick={() => setFilters({
    ...filters,
    status: statusFilter
  })} data-id="element-6845">
      <div className="flex justify-between items-start" data-id="element-6846">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-6847">
          {icon}
        </div>
      </div>
      <div data-id="element-6848">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-6849">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-6850">{value}</div>
      </div>
    </Card>;
  const handleExport = () => {
    success(`Экспорт ${filteredQueue.length} заявок завершён`);
  };
  const handleApprove = (request: any) => {
    setStatusOverrides(prev => ({
      ...prev,
      [request.id]: 'approved'
    }));
    success(`✅ Заявка ${request.astrologer || request.name} одобрена`);
  };
  const handleReject = (request: any) => {
    setStatusOverrides(prev => ({
      ...prev,
      [request.id]: 'rejected'
    }));
    success(`❌ Заявка ${request.astrologer || request.name} отклонена`);
  };
  const handleRequestInfo = (request: any) => {
    setStatusOverrides(prev => ({
      ...prev,
      [request.id]: 'needs_info'
    }));
    success(`📋 Запрошена дополнительная информация у ${request.astrologer || request.name}`);
  };
  // Bulk Actions
  const toggleAll = () => {
    if (selectedIds.size === currentQueue.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentQueue.map(v => v.id)));
    }
  };
  const toggleOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleBulkApprove = () => {
    const updates: Record<string, string> = {};
    selectedIds.forEach(id => {
      updates[id] = 'approved';
    });
    setStatusOverrides(prev => ({
      ...prev,
      ...updates
    }));
    success(`✅ Одобрено заявок: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  const handleBulkReject = () => {
    const updates: Record<string, string> = {};
    selectedIds.forEach(id => {
      updates[id] = 'rejected';
    });
    setStatusOverrides(prev => ({
      ...prev,
      ...updates
    }));
    success(`❌ Отклонено заявок: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  const handleBulkRequestInfo = () => {
    const updates: Record<string, string> = {};
    selectedIds.forEach(id => {
      updates[id] = 'needs_info';
    });
    setStatusOverrides(prev => ({
      ...prev,
      ...updates
    }));
    success(`📋 Запрошена информация для заявок: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  // Filtering
  const filteredQueue = enrichedQueue.filter(v => {
    const matchesSearch = (v.astrologer || v.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (filters.status !== 'all' && v.status !== filters.status) return false;
    if (filters.docType !== 'all' && !v.docTypes.includes(filters.docType)) return false;
    return true;
  });
  const totalPages = Math.ceil(filteredQueue.length / itemsPerPage);
  const currentQueue = filteredQueue.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="space-y-6 relative" data-id="element-6851">
      <div className="flex justify-between items-center" data-id="element-6852">
        <div data-id="element-6853">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-6854">Верификация</h2>
          <p className="text-sm text-slate-500" data-id="element-6855">
            Проверка документов и квалификации астрологов
          </p>
        </div>
      </div>

      {/* KPI Cards (Clickable) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-6856">
        {renderKpiCard('Все заявки', enrichedQueue.length, <FileText className="w-4 h-4 text-slate-600" data-id="element-6857" />, 'bg-slate-100', 'text-slate-600', 'all')}
        {renderKpiCard('На проверке', pendingReview, <Clock className="w-4 h-4 text-amber-600" data-id="element-6858" />, 'bg-amber-50', 'text-amber-600', 'pending')}
        {renderKpiCard('Нужно дополнить', needsMoreInfo, <AlertCircle className="w-4 h-4 text-orange-600" data-id="element-6859" />, 'bg-orange-50', 'text-orange-600', 'needs_info')}
        {renderKpiCard('Одобрено', approvedCount, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-6860" />, 'bg-emerald-50', 'text-emerald-600', 'approved')}
        {renderKpiCard('Отклонено', rejectedCount, <XCircle className="w-4 h-4 text-rose-600" data-id="element-6861" />, 'bg-rose-50', 'text-rose-600', 'rejected')}
      </div>

      {/* Filter Bar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-6862">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-6863">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-6864">
            <div className="relative flex-1 max-w-md" data-id="element-6865">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-6866" />
              <input type="text" placeholder="Поиск по имени астролога, ID заявки..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6867" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-6869" />} onClick={() => setShowFilters(!showFilters)} data-id="element-6868">
              Фильтры
            </Button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-6870">
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6872" />} onClick={handleExport} data-id="element-6871">
              Экспорт CSV
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-6873">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-6874">
              <div data-id="element-6875">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6876">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-6877">
                  <option value="all" data-id="element-6878">Все</option>
                  <option value="pending" data-id="element-6879">На проверке</option>
                  <option value="needs_info" data-id="element-6880">Нужно дополнить</option>
                  <option value="approved" data-id="element-6881">Одобрено</option>
                  <option value="rejected" data-id="element-6882">Отклонено</option>
                </select>
              </div>
              <div data-id="element-6883">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6884">
                  Тип документа
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.docType} onChange={e => setFilters({
              ...filters,
              docType: e.target.value
            })} data-id="element-6885">
                  <option value="all" data-id="element-6886">Все типы</option>
                  <option value="Паспорт" data-id="element-6887">Паспорт</option>
                  <option value="Диплом" data-id="element-6888">Диплом</option>
                  <option value="Сертификат" data-id="element-6889">Сертификат</option>
                  <option value="Видео-презентация" data-id="element-6890">Видео-презентация</option>
                </select>
              </div>
              <div data-id="element-6891">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6892">
                  Язык
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.language} onChange={e => setFilters({
              ...filters,
              language: e.target.value
            })} data-id="element-6893">
                  <option value="all" data-id="element-6894">Все языки</option>
                  <option value="ru" data-id="element-6895">Русский</option>
                  <option value="en" data-id="element-6896">Английский</option>
                </select>
              </div>
              <div className="flex items-end" data-id="element-6897">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              docType: 'all',
              language: 'all',
              country: 'all'
            })} data-id="element-6898">
                  Сбросить фильтры
                </Button>
              </div>
            </div>
          </div>}
      </Card>

      {/* Data Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-6899">
        <div className="overflow-x-auto" data-id="element-6900">
          <table className="w-full text-left whitespace-nowrap" data-id="element-6901">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-6902">
              <tr data-id="element-6903">
                <th className="px-4 py-3 w-10" data-id="element-6904">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.size === currentQueue.length && currentQueue.length > 0} onChange={toggleAll} data-id="element-6905" />
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-6906">ID / Ожидание</th>
                <th className="px-4 py-3 font-medium" data-id="element-6907">Астролог</th>
                <th className="px-4 py-3 font-medium" data-id="element-6908">Тип документов</th>
                <th className="px-4 py-3 font-medium" data-id="element-6909">AI-проверка</th>
                <th className="px-4 py-3 font-medium" data-id="element-6910">Последнее действие</th>
                <th className="px-4 py-3 font-medium" data-id="element-6911">Модератор</th>
                <th className="px-4 py-3 font-medium" data-id="element-6912">Статус</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-6913">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6914">
              {currentQueue.length === 0 ? <tr data-id="element-6915">
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500 text-sm" data-id="element-6916">
                    Очередь верификации пуста
                  </td>
                </tr> : currentQueue.map(v => <tr key={v.id} className={`hover:bg-slate-50 transition-colors group ${selectedIds.has(v.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-6917">
                    <td className="px-4 py-3" data-id="element-6918">
                      <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.has(v.id)} onChange={() => toggleOne(v.id)} data-id="element-6919" />
                    </td>
                    <td className="px-4 py-3" data-id="element-6920">
                      <div className="text-xs font-medium text-slate-900" data-id="element-6921">
                        VRF-{v.id}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1" data-id="element-6922">
                        <span className={`w-2 h-2 rounded-full ${v.waitStatus === 'good' ? 'bg-emerald-500' : v.waitStatus === 'warning' ? 'bg-amber-500' : 'bg-rose-500 animate-pulse'}`} data-id="element-6923" />
                        <span className="text-[10px] text-slate-500" data-id="element-6924">
                          {v.waitTime}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6925">
                      <div className="flex items-center gap-2" data-id="element-6926">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs flex-shrink-0" data-id="element-6927">
                          {(v.astrologer || v.name || '').split(' ').map((n: string) => n[0]).join('')}
                        </div>
                        <div data-id="element-6928">
                          <div className="text-sm font-medium text-indigo-600 hover:underline cursor-pointer flex items-center gap-1" onClick={() => {
                      const match = astrologers.find(a => a.name === (v.astrologer || v.name));
                      if (match) {
                        setDetailPanel({
                          type: 'astrologer_profile',
                          title: `Профиль: ${match.name}`,
                          data: match
                        });
                      }
                      setActiveTab('astrologers');
                    }} data-id="element-6929">
                            {v.astrologer || v.name}
                            <ExternalLink className="w-3 h-3 opacity-50" data-id="element-6930" />
                          </div>
                          {v.riskIndicators.length > 0 && <div className="flex items-center gap-1 mt-0.5 text-[10px] text-rose-600 font-medium" title={v.riskIndicators.join(', ')} data-id="element-6931">
                              <AlertTriangle className="w-3 h-3" data-id="element-6932" />
                              Риск: {v.riskIndicators[0]}
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6933">
                      <div className="flex flex-wrap gap-1 max-w-[150px]" data-id="element-6934">
                        {v.docTypes.map((type: string, idx: number) => <Badge key={idx} variant="neutral" size="sm" className="text-[9px] px-1.5 py-0" data-id="element-6935">
                            {type}
                          </Badge>)}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6936">
                      <Badge variant={v.aiConfidence > 90 ? 'success' : 'warning'} size="sm" className="flex items-center gap-1 w-fit" data-id="element-6937">
                        <Brain className="w-3 h-3" data-id="element-6938" />
                        {v.aiConfidence}%
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-6939">
                      <div className="text-xs text-slate-700 max-w-[150px] truncate" title={v.lastAction} data-id="element-6940">
                        {v.lastAction}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6941">
                      <div className="text-xs text-slate-600 flex items-center gap-1.5" data-id="element-6942">
                        {v.moderator === 'Система' ? <Brain className="w-3 h-3 text-slate-400" data-id="element-6943" /> : <ShieldCheck className="w-3 h-3 text-slate-400" data-id="element-6944" />}
                        {v.moderator}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6945">
                      <Badge variant={v.status === 'approved' ? 'success' : v.status === 'rejected' ? 'error' : v.status === 'needs_info' ? 'warning' : 'default'} size="sm" data-id="element-6946">
                        {v.status === 'approved' ? 'Одобрено' : v.status === 'rejected' ? 'Отклонено' : v.status === 'needs_info' ? 'Нужно дополнить' : 'На проверке'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-6947">
                      <div className="flex items-center justify-end gap-1" data-id="element-6948">
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" icon={<Eye className="w-4 h-4" data-id="element-6950" />} onClick={() => setPreviewData(v)} title="Быстрый просмотр" data-id="element-6949" />
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-emerald-600 hover:bg-emerald-50" icon={<CheckCircle2 className="w-4 h-4" data-id="element-6952" />} onClick={() => handleApprove(v)} title="Одобрить" data-id="element-6951" />
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-orange-500 hover:bg-orange-50" icon={<HelpCircle className="w-4 h-4" data-id="element-6954" />} onClick={() => handleRequestInfo(v)} title="Запросить доп. информацию" data-id="element-6953" />
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-rose-600 hover:bg-rose-50" icon={<XCircle className="w-4 h-4" data-id="element-6956" />} onClick={() => handleReject(v)} title="Отклонить" data-id="element-6955" />
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-6957">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6958" />
          </div>}
      </Card>

      {/* Floating Bulk Actions Bar */}
      {selectedIds.size > 0 && <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-40 animate-in slide-in-from-bottom-10 fade-in" data-id="element-6959">
          <span className="font-medium text-sm" data-id="element-6960">
            {selectedIds.size} выбрано
          </span>
          <div className="w-px h-6 bg-slate-700" data-id="element-6961" />
          <Button size="sm" variant="primary" onClick={handleBulkApprove} data-id="element-6962">
            Одобрить
          </Button>
          <Button size="sm" variant="outline" className="text-white border-slate-600 hover:bg-slate-800" onClick={handleBulkRequestInfo} data-id="element-6963">
            Запросить доп.
          </Button>
          <Button size="sm" variant="outline" className="text-rose-400 border-rose-800 hover:bg-rose-950 hover:text-rose-300" onClick={handleBulkReject} data-id="element-6964">
            Отклонить
          </Button>
        </div>}

      {/* Document Viewer Modal */}
      {viewingDoc && <div className="fixed inset-0 bg-slate-900/70 z-[60] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onClick={e => {
      if (e.target === e.currentTarget) setViewingDoc(null);
    }} data-id="element-6965">
          <Card className="w-full max-w-lg shadow-2xl overflow-hidden" padding="none" data-id="element-6966">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-6967">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-6968">
                <span className="text-2xl" data-id="element-6969">{viewingDoc.icon}</span>
                {viewingDoc.type}
              </h3>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-full" onClick={() => setViewingDoc(null)} data-id="element-6970">
                <X className="w-5 h-5" data-id="element-6971" />
              </Button>
            </div>
            <div className={`${viewingDoc.bg} p-8 flex flex-col items-center justify-center min-h-[400px]`} data-id="element-6972">
              <div className="text-6xl mb-6" data-id="element-6973">{viewingDoc.icon}</div>
              <div className="w-full max-w-xs space-y-2 bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm" data-id="element-6974">
                {viewingDoc.lines.map((line, li) => <div key={li} className="text-sm text-slate-600 text-center font-mono leading-relaxed" data-id="element-6975">
                    {line}
                  </div>)}
              </div>
              <p className="text-xs text-slate-400 mt-4" data-id="element-6976">
                {viewingDoc.type === 'Видео-презентация' ? 'MP4 · 48.2 MB' : 'PDF · 2.4 MB'}{' '}
                · Загружен 13 Окт 2024
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 bg-white flex justify-end gap-2" data-id="element-6977">
              <Button variant="outline" size="sm" onClick={() => setViewingDoc(null)} data-id="element-6978">
                Закрыть
              </Button>
              <Button variant="primary" size="sm" icon={<Download className="w-4 h-4" data-id="element-6980" />} data-id="element-6979">
                Скачать оригинал
              </Button>
            </div>
          </Card>
        </div>}

      {/* Quick Preview Modal */}
      {previewData && <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onClick={e => {
      if (e.target === e.currentTarget) setPreviewData(null);
    }} data-id="element-6981">
          <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" padding="none" data-id="element-6982">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-6983">
              <div className="flex items-center gap-3" data-id="element-6984">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-6985">
                  Документы астролога
                </h3>
                <Badge variant="neutral" data-id="element-6986">VRF-{previewData.id}</Badge>
              </div>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-full" onClick={() => setPreviewData(null)} data-id="element-6987">
                <X className="w-5 h-5" data-id="element-6988" />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 flex flex-col md:flex-row gap-6" data-id="element-6989">
              {/* Left Col: Documents & AI Check */}
              <div className="flex-1 space-y-6" data-id="element-6990">
                {/* AI & Risk Check */}
                <div className="grid grid-cols-2 gap-4" data-id="element-6991">
                  <Card padding="sm" className="border-indigo-100 bg-indigo-50/50" data-id="element-6992">
                    <div className="flex items-center gap-2 mb-2 text-indigo-900 font-semibold text-sm" data-id="element-6993">
                      <Brain className="w-4 h-4" data-id="element-6994" /> AI Document Check
                    </div>
                    <div className="space-y-2 text-xs text-indigo-800" data-id="element-6995">
                      <div className="flex justify-between" data-id="element-6996">
                        <span data-id="element-6997">Подлинность:</span>
                        <span className="font-bold" data-id="element-6998">
                          {previewData.aiConfidence}%
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-6999">
                        <span data-id="element-7000">Совпадение имени:</span>
                        <span className="font-bold text-emerald-600" data-id="element-7001">Да</span>
                      </div>
                      <div className="flex justify-between" data-id="element-7002">
                        <span data-id="element-7003">Текст сертификатов:</span>
                        <span className="font-bold text-emerald-600" data-id="element-7004">OK</span>
                      </div>
                    </div>
                  </Card>
                  <Card padding="sm" className={previewData.riskIndicators.length > 0 ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'} data-id="element-7005">
                    <div className={`flex items-center gap-2 mb-2 font-semibold text-sm ${previewData.riskIndicators.length > 0 ? 'text-rose-900' : 'text-emerald-900'}`} data-id="element-7006">
                      <ShieldCheck className="w-4 h-4" data-id="element-7007" /> Risk-контроль
                    </div>
                    {previewData.riskIndicators.length > 0 ? <ul className="text-xs text-rose-700 list-disc list-inside space-y-1" data-id="element-7008">
                        {previewData.riskIndicators.map((r: string, idx: number) => <li key={idx} data-id="element-7009">{r}</li>)}
                      </ul> : <div className="text-xs text-emerald-700" data-id="element-7010">
                        Подозрительных факторов не обнаружено
                      </div>}
                  </Card>
                </div>

                {/* Documents Grid */}
                <div data-id="element-7011">
                  <h4 className="font-semibold text-slate-900 mb-3 text-sm" data-id="element-7012">
                    Загруженные файлы
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-7013">
                    {previewData.docTypes.map((type: string, idx: number) => {
                  const docStyles: Record<string, {
                    bg: string;
                    border: string;
                    icon: string;
                    lines: string[];
                  }> = {
                    Паспорт: {
                      bg: 'bg-rose-50',
                      border: 'border-rose-200',
                      icon: '🪪',
                      lines: ['ПАСПОРТ', 'Российская Федерация', '━━━━━━━━━━━━━━', 'Фамилия: ██████████', 'Имя: ████████', 'Дата рождения: ██.██.████', '━━━━━━━━━━━━━━', 'Серия: ████  №: ██████']
                    },
                    Диплом: {
                      bg: 'bg-blue-50',
                      border: 'border-blue-200',
                      icon: '🎓',
                      lines: ['ДИПЛОМ', 'о профессиональной переподготовке', '━━━━━━━━━━━━━━', '████████████████', 'Специальность:', 'Астрология и консультирование', '━━━━━━━━━━━━━━', 'Рег. №: ██████-████']
                    },
                    Сертификат: {
                      bg: 'bg-amber-50',
                      border: 'border-amber-200',
                      icon: '📜',
                      lines: ['СЕРТИФИКАТ', 'подтверждает квалификацию', '━━━━━━━━━━━━━━', '████████████████', 'Курс: Натальная астрология', 'Часов: 240', '━━━━━━━━━━━━━━', '№ серт.: AST-██████']
                    },
                    'Видео-презентация': {
                      bg: 'bg-purple-50',
                      border: 'border-purple-200',
                      icon: '🎬',
                      lines: ['▶  ВИДЕО', 'Презентация астролога', '━━━━━━━━━━━━━━', 'Длительность: 3:42', 'Формат: MP4', '━━━━━━━━━━━━━━', 'Размер: 48.2 MB']
                    }
                  };
                  const style = docStyles[type] || {
                    bg: 'bg-slate-50',
                    border: 'border-slate-200',
                    icon: '📄',
                    lines: ['Документ', '━━━━━━━━━━━━━━', '████████████████']
                  };
                  return <Card key={idx} padding="sm" className={`${style.border} hover:shadow-md transition-all cursor-pointer group`} onClick={() => setViewingDoc({
                    type,
                    lines: style.lines,
                    icon: style.icon,
                    bg: style.bg
                  })} data-id="element-7014">
                          <div className={`h-40 ${style.bg} rounded-lg flex flex-col items-center justify-center mb-3 p-3 relative overflow-hidden`} data-id="element-7015">
                            <div className="text-3xl mb-2" data-id="element-7016">{style.icon}</div>
                            <div className="w-full space-y-1" data-id="element-7017">
                              {style.lines.map((line, li) => <div key={li} className="text-[8px] text-slate-400 text-center font-mono leading-tight truncate" data-id="element-7018">
                                  {line}
                                </div>)}
                            </div>
                            <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg" data-id="element-7019">
                              <div className="bg-white rounded-full p-2 shadow-lg" data-id="element-7020">
                                <Eye className="w-5 h-5 text-slate-700" data-id="element-7021" />
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-medium text-slate-900" data-id="element-7022">
                            {type}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-7023">
                            Загружен 13 Окт 2024 ·{' '}
                            {type === 'Видео-презентация' ? 'MP4 · 48.2 MB' : 'PDF · 2.4 MB'}
                          </div>
                        </Card>;
                })}
                  </div>
                </div>
              </div>

              {/* Right Col: History */}
              <div className="w-full md:w-80 flex-shrink-0" data-id="element-7024">
                <Card padding="sm" className="border-slate-200" data-id="element-7025">
                  <h4 className="font-semibold text-slate-900 mb-4 text-sm flex items-center gap-2" data-id="element-7026">
                    <Clock className="w-4 h-4 text-slate-400" data-id="element-7027" /> История
                    проверки
                  </h4>
                  <div className="space-y-0" data-id="element-7028">
                    {previewData.history.map((h: {
                  date: string;
                  text: string;
                  actor: string;
                }, idx: number) => <div key={idx} className="flex gap-3 pb-4 relative" data-id="element-7029">
                          {idx < previewData.history.length - 1 && <div className="absolute left-[7px] top-4 w-px h-full bg-slate-200" data-id="element-7030" />}
                          <div className="w-4 h-4 rounded-full bg-indigo-100 border-2 border-indigo-400 flex-shrink-0 mt-0.5 relative z-10" data-id="element-7031" />
                          <div data-id="element-7032">
                            <div className="text-xs font-medium text-slate-900" data-id="element-7033">
                              {h.text}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-7034">
                              {h.date} · {h.actor}
                            </div>
                          </div>
                        </div>)}
                  </div>
                </Card>

                {/* Quick Actions in Modal */}
                <div className="mt-4 space-y-2" data-id="element-7035">
                  <Button variant="primary" size="sm" fullWidth icon={<CheckCircle2 className="w-4 h-4" data-id="element-7037" />} onClick={() => {
                handleApprove(previewData);
                setPreviewData(null);
              }} data-id="element-7036">
                    Одобрить заявку
                  </Button>
                  <Button variant="outline" size="sm" fullWidth icon={<HelpCircle className="w-4 h-4" data-id="element-7039" />} onClick={() => {
                handleRequestInfo(previewData);
                setPreviewData(null);
              }} data-id="element-7038">
                    Запросить дополнение
                  </Button>
                  <Button variant="outline" size="sm" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" icon={<XCircle className="w-4 h-4" data-id="element-7041" />} onClick={() => {
                handleReject(previewData);
                setPreviewData(null);
              }} data-id="element-7040">
                    Отклонить заявку
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}